import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mountain, Footprints, ShieldCheck } from "lucide-react";
import Logo from "../../../assets/images/logo/light-logo-full.png";

const navLinks = [
  { name: "Home", href: "/" },
  { name: "About Us", href: "/about" },
  {
    name: "Adventures",
    dropdown: [
      {
        label: "Expeditions",
        sub: "Book your next summit",
        href: "/adventures/expeditions",
        icon: Mountain,
      },
      {
        label: "Trekking",
        sub: "Trek to the world’s greatest mountain basecamps",
        href: "/adventures/trekking",
        icon: Footprints,
      },
      {
        label: "Training Programs",
        sub: "Prepare with expert-led skill development",
        href: "/adventures/training-programs",
        icon: ShieldCheck,
      },
    ],
  },
  { name: "Services", href: "/services" },
  { name: "Blogs", href: "/blogs" },
  { name: "Gallery", href: "/gallery" },
  { name: "Expedition Essentials", href: "/expedition-gear"},
  { name: "Enquire", href: "/contact" },
];

const Navbar = () => {
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const pathname = window.location.pathname;

  /* =========================
     ACTIVE ROUTE HELPERS
     ========================= */
  const isActiveLink = (href) => {
    if (!href) return false;
    if (href === "/") return pathname === "/";
    return pathname.startsWith(href);
  };

  const isActiveDropdown = (dropdown = []) =>
    dropdown.some((item) => pathname.startsWith(item.href));

  /* =========================
     BREAKPOINT MATCH (TABLET + MOBILE)
     ========================= */
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const activeDropdown = navLinks.find(
    (link) => link.name === dropdownOpen && link.dropdown
  );

  const isExpeditionGearPage =
  pathname === "/exp" || pathname.startsWith("/expedition-gear");

  return (
  <motion.nav
      className={`fixed top-0 left-0 w-full z-[999] transition-all duration-300 shadow-md backdrop-blur-xl
        ${isExpeditionGearPage ? "bg-gray-800/30" : "bg-white/10"}
      `}
      onMouseLeave={() => setDropdownOpen(null)}
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Top Bar */}
        <div className="flex items-center justify-between py-4">
          <img src={Logo} alt="Logo" className="h-10 md:h-12" />

          {/* DESKTOP NAV (LG ONLY) */}
          <div className="hidden lg:flex space-x-10 text-white">
            {navLinks.map((link) => {
              const hasDropdown = !!link.dropdown;
              const isActive = hasDropdown
                ? isActiveDropdown(link.dropdown)
                : isActiveLink(link.href);

              return (
                <div
                  key={link.name}
                  className="relative"
                  onMouseEnter={() => {
                    if (hasDropdown) setDropdownOpen(link.name);
                    else setDropdownOpen(null);
                  }}
                >
                  {hasDropdown ? (
                    <motion.div
                      className="cursor-pointer relative"
                      initial="rest"
                      animate={isActive ? "active" : "rest"}
                      whileHover="hover"
                    >
                      {link.name}
                      <motion.span
                        className="absolute left-0 -bottom-1 h-[2px] bg-[#5b91b3]"
                        variants={{
                          rest: { width: 0, opacity: 0 },
                          hover: { width: "100%", opacity: 1 },
                          active: { width: "100%", opacity: 1 },
                        }}
                        transition={{ duration: 0.25 }}
                      />
                    </motion.div>
                  ) : (
                    <motion.a
                      href={link.href}
                      className="cursor-pointer relative"
                      initial="rest"
                      animate={isActive ? "active" : "rest"}
                      whileHover="hover"
                    >
                      {link.name}
                      <motion.span
                        className="absolute left-0 -bottom-1 h-[2px] bg-[#5b91b3]"
                        variants={{
                          rest: { width: 0, opacity: 0 },
                          hover: { width: "100%", opacity: 1 },
                          active: { width: "100%", opacity: 1 },
                        }}
                        transition={{ duration: 0.25 }}
                      />
                    </motion.a>
                  )}
                </div>
              );
            })}
          </div>

          {/* CTA (LG ONLY) */}
          <a
            href="/contact"
            className="hidden lg:block border border-white px-6 py-2 rounded-full hover:bg-white hover:text-black transition"
          >
            Book Now
          </a>

          {/* HAMBURGER (MOBILE + TABLET) */}
          <div className="lg:hidden mt-1">
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? (
                <svg width="34" height="34" viewBox="0 0 34 34">
                  <>
                    <line x1="6" y1="6" x2="28" y2="28" stroke="white" strokeWidth="3" strokeLinecap="round" />
                    <line x1="28" y1="6" x2="6" y2="28" stroke="white" strokeWidth="3" strokeLinecap="round" />
                  </>
                </svg>
              ) : (
                <svg width="42" height="28" viewBox="0 0 56 34">
                  <>
                    <line x1="0" y1="6" x2="30" y2="6" stroke="white" strokeWidth="3" strokeLinecap="round" />
                    <line x1="0" y1="17" x2="48" y2="17" stroke="white" strokeWidth="3" strokeLinecap="round" />
                    <line x1="22" y1="28" x2="48" y2="28" stroke="white" strokeWidth="3" strokeLinecap="round" />
                  </>
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* DESKTOP DROPDOWN (LG ONLY) */}
        <AnimatePresence>
          {activeDropdown && !isMobile && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.28, ease: "easeOut" }}
              className="relative overflow-hidden py-3"
              style={{ width: "550px", marginLeft: 310 }}
            >
              {activeDropdown.dropdown.map((item) => {
                const Icon = item.icon;
                return (
                  <motion.a
                    key={item.label}
                    href={item.href}
                    onClick={() => setDropdownOpen(null)}
                    whileHover={{ backgroundColor: "rgba(255,255,255,0.06)" }}
                    className="flex items-center gap-3 mb-3 bg-[#16131330] px-6 py-3 rounded-lg"
                  >
                    {Icon && <Icon size={20} className="text-[#5b91b3]" />}
                    <div>
                      <span className="font-medium">{item.label}</span>
                      <span className="block text-xs text-gray-300">{item.sub}</span>
                    </div>
                  </motion.a>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* MOBILE + TABLET MENU */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="lg:hidden bg-[#1b3144] py-6 text-white text-center space-y-6"
          >
            {navLinks.map((link) => (
              <div key={link.name}>
                {link.dropdown ? (
                  <>
                    <button
                      className="text-lg w-full"
                      onClick={() =>
                        setDropdownOpen(
                          dropdownOpen === link.name ? null : link.name
                        )
                      }
                    >
                      {link.name}
                    </button>
                    <AnimatePresence>
                      {dropdownOpen === link.name && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="mt-3 space-y-2"
                        >
                          {link.dropdown.map((item) => (
                            <a
                              key={item.label}
                              href={item.href}
                              onClick={() => setMobileMenuOpen(false)}
                              className="block text-sm text-gray-300 hover:text-white"
                            >
                              {item.label}
                            </a>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>
                ) : (
                  <a
                    href={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-lg block"
                  >
                    {link.name}
                  </a>
                )}
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
